import { Control, useController, useFieldArray } from 'react-hook-form';

import { RcsCampaignDetailsFormValues } from './types';
import {
  createMaxLengthValidator,
  createWebsiteValidator,
  isRequired,
  isValidInput,
} from '../../validators';

export const useControllers = (
  control: Control<
    RcsCampaignDetailsFormValues,
    any,
    RcsCampaignDetailsFormValues
  >
) => {
  const {
    field: {
      value: displayName,
      onChange: setDisplayName,
      onBlur: validateDisplayName,
    },
    fieldState: { error: displayNameError },
  } = useController({
    control,
    name: 'displayName',
    rules: {
      validate: {
        isRequired,
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(40),
      },
    },
  });
  const {
    field: { value: color, onChange: setColor },
    fieldState: { error: colorError },
  } = useController({
    control,
    name: 'color',
    rules: {
      validate: {
        isRequired,
      },
    },
  });
  const {
    field: {
      value: shortDescription,
      onChange: setShortDescription,
      onBlur: validateShortDescription,
    },
    fieldState: { error: shortDescriptionError },
  } = useController({
    control,
    name: 'shortDescription',
    rules: {
      validate: {
        isRequired,
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(100),
      },
    },
  });
  const {
    field: {
      value: termsAndConditionsLink,
      onChange: setTermsAndConditionsLink,
      onBlur: validateTermsAndConditionsLink,
    },
    fieldState: { error: termsAndConditionsLinkError },
  } = useController({
    control,
    name: 'termsAndConditionsLink',
    rules: {
      validate: {
        isRequired,
        withValidUrl: createWebsiteValidator('Invalid Terms of Use URL'),
      },
    },
  });
  const {
    field: {
      value: privacyPolicyLink,
      onChange: setPrivacyPolicyLink,
      onBlur: validatePrivacyPolicyLink,
    },
    fieldState: { error: privacyPolicyLinkError },
  } = useController({
    control,
    name: 'privacyPolicyLink',
    rules: {
      validate: {
        isRequired,
        withValidUrl: createWebsiteValidator('Invalid Privacy Policy URL'),
      },
    },
  });
  const {
    fields: phonePairs,
    append: addPhonePair,
    remove: removePhonePair,
  } = useFieldArray({
    control,
    name: 'phonePairs',
  });
  const {
    fields: emailPairs,
    append: addEmailPair,
    remove: removeEmailPair,
  } = useFieldArray({
    control,
    name: 'emailPairs',
  });
  const {
    fields: websitePairs,
    append: addWebsitePair,
    remove: removeWebsitePair,
  } = useFieldArray({
    control,
    name: 'websitePairs',
  });
  const {
    field: { value: campaignLogo, onChange: setCampaignLogo },
    fieldState: { error: campaignLogoError },
  } = useController({
    control,
    name: 'campaignLogo',
    rules: {
      validate: {
        isRequired,
      },
    },
  });
  const {
    field: { value: campaignBanner, onChange: setCampaignBanner },
    fieldState: { error: campaignBannerError },
  } = useController({
    control,
    name: 'campaignBanner',
    rules: {
      validate: {
        isRequired,
      },
    },
  });

  return {
    displayName,
    setDisplayName,
    validateDisplayName,
    displayNameError,
    color,
    setColor,
    colorError,
    shortDescription,
    setShortDescription,
    validateShortDescription,
    shortDescriptionError,
    termsAndConditionsLink,
    setTermsAndConditionsLink,
    validateTermsAndConditionsLink,
    termsAndConditionsLinkError,
    privacyPolicyLink,
    setPrivacyPolicyLink,
    validatePrivacyPolicyLink,
    privacyPolicyLinkError,
    phonePairs,
    addPhonePair,
    removePhonePair,
    emailPairs,
    addEmailPair,
    removeEmailPair,
    websitePairs,
    addWebsitePair,
    removeWebsitePair,
    campaignLogo,
    setCampaignLogo,
    campaignLogoError,
    campaignBanner,
    setCampaignBanner,
    campaignBannerError,
  };
};
